import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { MaskedLines, Reveal } from "@/components/motion";
import { IMAGES } from "@/data/site";

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 260]);

  return (
    <section className="relative flex min-h-[100svh] items-end overflow-hidden bg-navy" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Construction workforce on a large commercial construction site"
          className="h-[115%] w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-navy/25" />
      <div className="absolute inset-0 bg-blueprint-dark opacity-30" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-44">
        <Reveal y={14} className="flex items-center gap-4">
          <span className="h-px w-14 bg-gold" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.4em] text-gold sm:text-xs">
            National Construction Labour Hire & Workforce Solutions
          </span>
        </Reveal>

        <MaskedLines
          delay={0.25}
          lines={["Construction Workforce.", { t: "Built for Performance.", c: "text-gold" }]}
          className="mt-8 font-heading text-[2.7rem] font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl"
        />

        <Reveal delay={0.7} className="mt-8 max-w-2xl">
          <p className="text-base leading-relaxed text-white/75 md:text-lg">
            From individual personnel requirements to large-scale workforce deployments, Formcore Constructions
            provides reliable, professionally managed personnel to construction businesses across Australia.
          </p>
        </Reveal>

        <Reveal delay={0.85} className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/contact"
            data-testid="hero-request-workforce-btn"
            className="inline-flex items-center gap-2 bg-gold px-8 py-4 font-heading text-sm font-bold uppercase tracking-[0.12em] text-navy transition-colors duration-200 hover:bg-gold-dark active:scale-[0.97]"
          >
            Request Workforce
            <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-contact-team-btn"
            className="inline-flex items-center gap-2 border border-white/40 px-8 py-4 font-heading text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors duration-200 hover:border-gold hover:text-gold active:scale-[0.97]"
          >
            Contact Our Team
          </Link>
        </Reveal>

        <Reveal delay={1} className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/15 pt-6">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-white/50">150+ Personnel in NSW</span>
          <span className="hidden h-3 w-px bg-white/20 sm:block" />
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-white/50">20+ Years Experience</span>
          <span className="hidden h-3 w-px bg-white/20 sm:block" />
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-white/50">NSW · QLD · VIC · ACT · Nationwide</span>
        </Reveal>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 right-8 z-10 hidden text-white/50 lg:block"
      >
        <ChevronDown className="h-6 w-6 animate-bounce" />
      </motion.div>
    </section>
  );
}
